import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { GlobalApplicationState } from 'globalApplicationState';
import { fetchImage, fetchImagesByIds } from 'modules/posts/actionCreator';
import ImageGalleryDialog from './imageGalleryDialog';
import ImageGalleryArrangementDialog from './imageGalleryArrangementDialog';
import { IAttachedGalleryFile, IGalleryItemDetails, Image } from 'modules/gallery';
import ReactDOMServer from "react-dom/server";
import InlineGallery from '../../inlineGallery';
import { getAttachmentFromFile } from 'utils/imageHelpers';
import { dropItemBackward, dropItemForward, useDragReorder } from 'modules/common/hooks/useDragReorder';
import uuid from 'uuid';
import { Editor } from 'tinymce';

interface IImageFetchDetails {
    id: string;
    caption: string;
}

const InlineImageGalleryWrapper: React.FunctionComponent<PropsWithRedux> = props => {
    const [arrangementOpen, setArrangementOpen] = React.useState(false);
    const [selectedImages, setSelectedImages] = React.useState<IAttachedGalleryFile[]>([]);
    const [draggedIdx, setDraggedIdx] = React.useState<number | null>(null);
    const [isFetching, setIsFetching] = React.useState<boolean>(false);

    const dropZoneClassName = React.useRef<string>(uuid());
    const isDragging = draggedIdx !== null && draggedIdx !== -1;

    const { galleryOpen, editGalleryDetails, editGalleryId, resetEditGallery, fetchImages, resetEditDetails, fetchOriginal } = props;

    //Reset selected images if either dialog is exited.
    React.useEffect(() => {
        if (!galleryOpen && !arrangementOpen && selectedImages.length > 0) {
            resetEditGallery();
            setSelectedImages([]);
        } 
    }, [galleryOpen, arrangementOpen, selectedImages, resetEditGallery]);

    React.useEffect(() => {
        const fetchAndConvertImages = async (imageFetchDetails: IImageFetchDetails[]) => {
            const ids = imageFetchDetails.map(ifd => ifd.id);
            let images = await fetchImages(ids);

            //Make the list of images match the order of the passed in ids (how it currently looks)
            images.sort((a, b) => {
                return ids.indexOf(a.id) - ids.indexOf(b.id)
            });

            let attachedImages: IAttachedGalleryFile[] = [];
            for (let i = 0; i < images.length; i++) {
                const image = images[i];

                const matchingEntry = editGalleryDetails!.filter(e => e.ospreyId === image.id)[0];
                image.url = matchingEntry.fileUrl;
                
                let attachment = await getAttachmentFromFile(image);
                if(attachment) {
                    attachment.caption = matchingEntry.caption;
                    attachedImages = attachedImages.concat(attachment);
                }
            }

            setSelectedImages(attachedImages);
            resetEditDetails();
        }

        if (editGalleryId && editGalleryDetails && selectedImages.length === 0 && !isFetching) {
            const imageFetchDetails = editGalleryDetails.map(e => ({id: e.ospreyId, caption: e.caption}));
            setIsFetching(true);
            
            //Using promise here since we can't make the top level useEffect async
            fetchAndConvertImages(imageFetchDetails).then(() => {
                setIsFetching(false);
            });
        }
    }, [editGalleryId, editGalleryDetails, fetchImages, resetEditDetails, selectedImages.length, isFetching]);

    const onHideArrangementDialog = () => setArrangementOpen(false);

    const onConfirmImageSelection = async (selectedImages: IAttachedGalleryFile[]) => {
        //Hiding of gallery is done by the gallery itself on confirm
        var transformedImages = await onUpdateOriginalUrls(selectedImages);
        setSelectedImages(transformedImages);
        setArrangementOpen(true);
    };

    const fetchOriginals = async (ids: string[]) => {
        const fetchingTasks: Promise<Image>[] = [];
        for (let id of ids) {
            fetchingTasks.push(fetchOriginal(id));
        }
        return await Promise.all(fetchingTasks);
    }

    const onUpdateOriginalUrls = async (selected: IAttachedGalleryFile[]): Promise<IAttachedGalleryFile[]> => {
        const originals = await fetchOriginals(selected.map(s => s.ospreyId));
        return selected.map(s => ({...s,
            originalFileUrl: originals.filter(o => o.id === s.ospreyId)[0]?.url ?? s.fileUrl}));
    };

    const onBackToImageEdit = () => {
        onHideArrangementDialog();
        props.onShowImageGalleryDialog();
    };

    const onRemoveImage = (image: IAttachedGalleryFile) => {
        const selectedImagesToSet = selectedImages.filter(s => s.blobId !== image.blobId);
        setSelectedImages(selectedImagesToSet);
    };

    const onSwapImages = (imageOne: IAttachedGalleryFile, imageTwo: IAttachedGalleryFile) => {
        const firstImageIndex = selectedImages.indexOf(imageOne);
        const secondImageIndex = selectedImages.indexOf(imageTwo);

        if (firstImageIndex === -1 || secondImageIndex === -1) {
            return selectedImages;
        }
        else {
            let selectedImagesClone = JSON.parse(JSON.stringify(selectedImages));
            selectedImagesClone[firstImageIndex] = imageTwo;
            selectedImagesClone[secondImageIndex] = imageOne;

            setSelectedImages(selectedImagesClone);
        }
    };

    const onInsertImages = () => {
        const itemDetails: IGalleryItemDetails[] = selectedImages.map((s) => ({
            fileUrl: s.fileUrl ?? "",
            ospreyId: s.ospreyId,
            caption: s?.caption ?? "",
            originalFileUrl: s?.originalFileUrl ?? s.fileUrl ?? ""
        }));
        const html = ReactDOMServer.renderToString(<InlineGallery selectedImageDetails={itemDetails} showInteractables={false}/>);

        if (props.editorRef && props.editorRef.current) {
            if (props.editGalleryId !== null) {
                let tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;
                let extractedElement = tempDiv.firstChild; 
                props.editorRef.current.dom.replace(extractedElement!, props.editGalleryId);
                props.editorRef.current.insertContent(""); //Editor won't think it's had changes made unless we call insertContent.
            }
            else {
                props.editorRef.current.insertContent(html);
            }
        }

        onHideArrangementDialog();
    };

    // re-order items
    const onDrag = (idx: number, _: string) => {
        document.body.style.cursor = "move";
        setDraggedIdx(idx);
    };

    const onDrop = (_: string, idx: number) => {
        const draggedIdxCopy = draggedIdx;
        setDraggedIdx(null);

        if (draggedIdxCopy !== null && draggedIdxCopy > -1) {
            let dragged = { ...selectedImages[draggedIdxCopy] };
            
            if (draggedIdxCopy > -1 && idx > -1 && dragged) {
                // dont do anything if band is in its original spot
                if (idx === draggedIdx || draggedIdxCopy === null) return;

                let newItems =
                    draggedIdxCopy < dropIdx
                        ? dropItemForward(selectedImages, draggedIdxCopy, dropIdx)
                        : dropItemBackward(selectedImages, draggedIdxCopy, dropIdx);

                setSelectedImages(newItems);
            }
        }
    };

    //useEffect to actually check the mouse and perform the actions - ondrop is defined above.
    const { mousePos, dropIdx } = useDragReorder<string>({
        enabled: true,
        draggedIdx: draggedIdx,
        items: selectedImages.map((s: IAttachedGalleryFile) => ({
            id: s.ospreyId,
        })),
        dropZoneClassName: dropZoneClassName.current,
        onDrop,
        isVertical: false
    });

    return (
        (props.galleryOpen || arrangementOpen) ?
            <React.Fragment>
                <ImageGalleryDialog 
                    key="imageGallery"
                    multiSelect 
                    applyText="Next"
                    show={props.galleryOpen} 
                    onApply={onConfirmImageSelection} 
                    onClose={props.onHideImageGalleryDialog} 
                    attachedContent={selectedImages}
                />
                <ImageGalleryArrangementDialog 
                    show={arrangementOpen} 
                    selectedImages={selectedImages} 
                    onClose={onHideArrangementDialog} 
                    onRemoveImage={onRemoveImage}
                    onBackToImageEdit={onBackToImageEdit}
                    onSwapImages={onSwapImages}
                    onInsertImages={onInsertImages}
                    onDrag={onDrag}
                    dropClass={dropZoneClassName.current}
                    draggedIdx={draggedIdx}
                    dropIdx={dropIdx}
                    isDragging={isDragging}
                    mousePos={mousePos}
                    activeLanguage={props.activeLanguage}
                />
            </React.Fragment>
            : null
    );
}

interface ImageGalleryWrapperProps {
    galleryOpen: boolean;
    activeLanguage: string;
    editorRef: React.MutableRefObject<Editor | null>;
    onShowImageGalleryDialog: () => void;
    onHideImageGalleryDialog: () => void;
    resetEditGallery: () => void;
    resetEditDetails: () => void;

    editGalleryDetails: IGalleryItemDetails[] | null;
    editGalleryId: string | null;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ImageGalleryWrapperProps) => ({
        ...ownProps,
    }), {
        fetchImages: fetchImagesByIds,
        fetchOriginal: fetchImage
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;
export default connector(InlineImageGalleryWrapper);